import React, { useMemo } from "react";
import styled from "styled-components";
import { useSearchParams } from "react-router-dom";
import { useGetMachineDetails } from "../../../hooks/useGetMachineDetails";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 90%;
  padding: 0.1rem;
  overflow: hidden;
  align-items: center;
  flex-grow: 1;
  flex-shrink: 1;
`;

const Error = styled.div`
  display: flex;
  height: 100%;
  width: 55%;
  margin-top: 4em;
  font-size: 0.5em;
  color: red;
`;

const CardHeading = styled.text`
  margin-top: 0.8em;
  font-size: 0.7em;
`;

const Heading = styled.div`
  font-weight: bold;
  font-size: 0.6em;
  padding: 0.2rem 0;
`;

const DataStyle = styled.text`
  font-size: 0.8em;
  font-weight: 100;
  padding-right: 0.8rem;
`;

const FaultContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 8fr 1fr;
  padding: 0.2rem;
`;

const ScrollableContainer = styled.div`
  max-height: 35rem; /* Adjust this height as needed */
  overflow-y: auto;
  width: 100%; /* Ensures it matches the parent width */
  padding: 2.5rem 1rem;
`;

function MIMachineFault() {
  const [searchParams] = useSearchParams();
  const machineParam = searchParams.get("m");
  const timePeriod = searchParams.get("timeperiod") || "Week";
  const { data, error, isLoading } = useGetMachineDetails({
    machine: machineParam,
    timePeriod: timePeriod,
  });

  // Prepare the chart data
  const chartData = useMemo(() => {
    if (!data) return [];

    // Sort and get top 4 with remaining as "Others"
    const sortedFaults = [...data.data.faultTypes].sort(
      (a, b) => b.faultOutputAvg - a.faultOutputAvg
    );
    const topFaults = sortedFaults.slice(0, 4);
    const otherFaultsSum = sortedFaults
      .slice(4)
      .reduce((sum, fault) => sum + fault.faultOutputAvg, 0);

    const pieData = topFaults.map((fault) => ({
      name: fault.faultMessage.replace("%", ""),
      value: fault.faultOutputAvg,
    }));

    if (otherFaultsSum > 0) {
      pieData.push({ name: "Others", value: otherFaultsSum });
    }

    return pieData;
  }, [data]);

  return (
    <Card>
      {isLoading && <div>Loading...</div>}
      {error && <Error>Error: {error.message}</Error>}
      {data && (
        <>
          <ResponsiveContainer width="100%" height={330}>
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                innerRadius={65} // Creates a donut shape
                fill="#8884d8"
                paddingAngle={5} // Adds space between slices for better appearance
                labelLine={false} // Removes labels from the chart
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#494848"][
                        index % 5
                      ]
                    }
                    stroke="#000"
                    strokeWidth={2} // Adds depth effect with a border
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend layout="vertical" align="center" verticalAlign="bottom" />
            </PieChart>
          </ResponsiveContainer>
          <ScrollableContainer>
            <FaultContainer>
              <FaultsList faultData={data.data.faultTypes} />
            </FaultContainer>
          </ScrollableContainer>
        </>
      )}
    </Card>
  );
}

function FaultsList({ faultData }) {
  return (
    <>
      <Heading></Heading>
      <Heading>Message</Heading>
      <Heading>Count</Heading>
      {faultData.map((fault, index) => (
        <React.Fragment key={index}>
          <DataStyle>{fault.faultOutputAvg.toFixed(2)}</DataStyle>
          <DataStyle>{fault.faultMessage.replace("%", "")}</DataStyle>
          <DataStyle>{fault.faultCount}</DataStyle>
        </React.Fragment>
      ))}
    </>
  );
}

export default MIMachineFault;
